import useSWRMutation from "swr/mutation";
import { useAccountIds } from "../../../../auth/accessToken/components/withAccountIds";
import useStore from "../../../../common/hooks/useExternalStore";
import createExternalStore from "../../../../common/utils/createExternalStore";
import { CORE_API_HOSTNAME } from "../../../../env";
import fulfill from "../../../../fulfill";
import { KlarnaConnectionSignup } from "../../../../types/management-auth/generated";
import useAccount from "../../../account/hooks/useAccount";

export enum ModalState {
    Closed = "Closed",
    InitiateApplicationLoading = "InitiateApplicationLoading",
    ApplicationFormOpened = "ApplicationFormOpened",
    ApplicationReceived = "ApplicationReceived",
}

const klarnaModelStore = createExternalStore(
    ModalState.Closed,
    {
        setState: (_, action: ModalState) => action,
    },
    "connections/klarna",
);

const fetchPostSignup = async (
    _key: string,
    options: {
        arg: {
            account_id: string;
            data: KlarnaConnectionSignup;
        };
    },
): Promise<boolean> => {
    return await fulfill.post({
        accountId: options.arg.account_id,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${options.arg.account_id}/management/settings/connections/klarna`,
        json: { signup: options.arg.data },
        handlers: {
            200: () => true,
            500: () => false,
            501: () => false,
            502: () => false,
            503: () => false,
            504: () => false,
        },
    });
};

export const useKlarnaConnection = () => {
    const { prodAccountId, urlAccountId } = useAccountIds();
    const { state: modalState, dispatch } = useStore(klarnaModelStore);
    const { account, mutate } = useAccount();
    const accountId = prodAccountId || urlAccountId;
    const {
        trigger: signupForConnection,
        isMutating: isSigningUpForConnection,
        error: signupForConnectionError,
        data: signupForConnectionResponse,
    } = useSWRMutation(
        `${accountId}/payment-connections/klarna/signup`,
        fetchPostSignup,
        {
            onSuccess: (response) => {
                if (response) {
                    dispatch("setState", ModalState.ApplicationReceived);
                } else {
                    dispatch("setState", ModalState.Closed);
                }
            },
        },
    );
    const openModal = () => {
        dispatch("setState", ModalState.ApplicationFormOpened);
    };

    const closeModal = () => {
        dispatch("setState", ModalState.Closed);
    };

    const signupForKlarnaConnection = async (data: KlarnaConnectionSignup) => {
        dispatch("setState", ModalState.InitiateApplicationLoading);
        signupForConnection({
            account_id: accountId,
            data,
        });
    };

    return {
        modalState,
        openModal,
        closeModal,
        signupForKlarnaConnection,
        isSigningUpForConnection,
        signupForConnectionError,
        signupForConnectionResponse,
        account,
    };
};
